import { alpha, type Theme } from '@mui/material/styles'
import * as Yup from 'yup'

export type CreateVisitField = {
  name: string
  label: string
  type: string
  yupValidation: any
  defaultValue?: any
  options?: { label: string; value: string }[]
  required?: boolean
  placeholder?: string
  multiline?: boolean
  gridCols?: any
  accept?: any
  disabled?: boolean
}
export const getAdaptiveShadow = (theme: Theme, elevation = 6) => {
  const color =
    theme.palette.mode === 'dark'
      ? theme.palette.common.white
      : theme.palette.common.black

  return `0px ${elevation / 2}px ${elevation * 2}px ${alpha(color, 0.2)}`
}

export const visitTypes = {
  OP_VISIT: 'OP_VISIT',
  IP_VISIT: 'IP_VISIT',
}

export const ACTION_ENUM = {
  CREATE: 'create',
  EDIT: 'edit',
  DELETE: 'delete',
  VIEW: 'view',
}

export const NOTIFICATION_TYPES = {
  ERROR: 'error',
  SUCCESS: 'success',
  INFO: 'info',
  WARNING: 'warning',
}

export const FULLSCREEN_TOGGGLE_TEXT = {
  FULL_SCREEN: 'Best viewed in fullscreen',
  EXIT_FULL_SCREEN: 'Exit Full Screen',
}

// utils/changeLottieColor.ts
export function changeLottieColor(
  animationData: any,
  newColor: number[] // allow any number[]
) {
  const deepClone = JSON.parse(JSON.stringify(animationData))

  function updateItems(items: any[]) {
    if (!items) return

    items?.forEach((item) => {
      if (item.ty === 'st' && item.c?.k) {
        item.c.k = newColor
      }
      if (item.ty === 'fl' && item.c?.k) {
        item.c.k = newColor
      }
      if (item.it) {
        updateItems(item.it)
      }
    })
  }

  deepClone.layers.forEach((layer: any) => {
    if (layer.shapes) {
      updateItems(layer.shapes)
    }
  })

  return deepClone
}

/* eslint-disable no-useless-escape */
export const opdRegistrationFormSections = (): {
  title: string
  fields: CreateVisitField[]
}[] => [
  {
    title: 'Patient Details',
    fields: [
      {
        name: 'medicoLegalCase',
        label: 'Medico Legal Case',
        type: 'checkbox',
        yupValidation: Yup.boolean(),
        defaultValue: false,
        gridCols: 'grid-item',
      },
      {
        name: 'emergencyPatient',
        label: 'Emergency Patient',
        type: 'checkbox',
        yupValidation: Yup.boolean(),
        defaultValue: false,
        gridCols: 'grid-item',
      },
      {
        name: 'unidentifiedPatient',
        label: 'Unidentified Patient',
        type: 'checkbox',
        yupValidation: Yup.boolean(),
        defaultValue: false,
        gridCols: 'grid-item',
      },
    ],
  },
  {
    title: 'Medico Legal Case Details',
    fields: [
      {
        name: 'medicoLegalCaseType',
        label: 'Medico Legal Case',
        type: 'autocomplete',
        // yupValidation: Yup.string().required("Medical Legal Case is required"),
        yupValidation: Yup.string(),
        defaultValue: '',
        options: [
          { label: 'Accident', value: 'accident' },
          { label: 'Assault', value: 'assault' },
          { label: 'Poisoning', value: 'poisoning' },
          { label: 'Burns', value: 'burns' },
          { label: 'Other', value: 'other' },
        ],
        gridCols: 'grid-item',
        required: false,
      },
      {
        name: 'mlcType',
        label: 'MLC Type',
        type: 'autocomplete',
        yupValidation: Yup.string(),
        defaultValue: '',
        options: [
          { label: 'Simple', value: 'simple' },
          { label: 'Grievous', value: 'grievous' },
          { label: 'Fatal', value: 'fatal' },
        ],

        gridCols: 'grid-item',
        required: false,
      },
      {
        name: 'accidentPlace',
        label: 'Accident Place',
        type: 'text',
        yupValidation: Yup.string(),
        defaultValue: '',
        gridCols: 'grid-item',
        placeholder: 'Enter Accident Place',
      },
      {
        name: 'policeStation',
        label: 'Police Station',
        type: 'text',
        yupValidation: Yup.string(),
        defaultValue: '',
        gridCols: 'grid-item',
        placeholder: 'Enter Police Station',
      },
      {
        name: 'mlcRemarks',
        label: 'MLC Remarks',
        type: 'text',
        yupValidation: Yup.string(),
        defaultValue: '',
        gridCols: 'grid-item',
        placeholder: 'Enter MLC Remarks',
      },
      {
        name: 'certificateStatus',
        label: 'Certificate Status',
        type: 'autocomplete',
        yupValidation: Yup.string(),
        options: [
          { label: 'Pending', value: 'pending' },
          { label: 'Issued', value: 'issued' },
          { label: 'Cancelled', value: 'cancelled' },
        ],
        defaultValue: '',
        gridCols: 'grid-item',
        required: false,
      },
    ],
  },
  {
    title: 'Basic Information',
    fields: [
      {
        name: 'title',
        label: 'Title',
        type: 'autocomplete',
        yupValidation: Yup.string().required('Title is required'),
        options: [
          { label: 'B/O', value: 'b/o' },
          { label: 'Baby', value: 'baby' },
          { label: 'Dr.', value: 'dr' },
          { label: 'Miss', value: 'miss' },
          { label: 'Mr.', value: 'mr' },
          { label: 'Mrs.', value: 'mrs' },
          { label: 'Ms.', value: 'ms' },
          { label: 'Master', value: 'master' },
        ],
        defaultValue: '',
        gridCols: 'grid-item',
        placeholder: 'Select Title',
        required: true,
      },
      {
        name: 'firstName',
        label: 'First Name',
        type: 'text',
        yupValidation: Yup.string()
          .required('First name is required')
          .min(2, 'Minimum 2 characters'),
        defaultValue: '',
        gridCols: 'grid-item',
        required: true,
      },
      {
        name: 'middleName',
        label: 'Middle Name',
        type: 'text',
        yupValidation: Yup.string(),
        defaultValue: '',
        gridCols: 'grid-item',
      },
      {
        name: 'lastName',
        label: 'Last Name',
        type: 'text',
        yupValidation: Yup.string()
          .required('Last name is required')
          .min(2, 'Minimum 2 characters'),
        // yupValidation: Yup.string(),
        defaultValue: '',
        gridCols: 'grid-item',
        required: true,
      },
      {
        name: 'mobile',
        label: 'Mobile',
        type: 'tel',
        yupValidation: Yup.string()
          .required('Mobile is required')
          .matches(/^[\+]?[0-9\s\-\(\)]{10,15}$/, 'Invalid phone number'),
        defaultValue: '',
        gridCols: 'grid-item',
        placeholder: '+919999999999',
        required: true,
      },
      {
        name: 'dateOfBirth',
        label: 'Date of Birth',
        type: 'date',
        yupValidation: Yup.string(),
        // yupValidation: Yup.string().required("Date of birth is required"),
        defaultValue: '',
        gridCols: 'grid-item',
        required: false,
      },
      {
        name: 'age',
        label: 'Age',
        type: 'number',
        yupValidation: Yup.string().required('Age is required'),
        defaultValue: '',
        gridCols: 'grid-item',
        required: true,
      },
      {
        name: 'gender',
        label: 'Gender',
        type: 'autocomplete',
        yupValidation: Yup.string().required('Gender is required'),
        defaultValue: '',
        options: [
          { label: 'Male', value: 'male' },
          { label: 'Female', value: 'female' },
          { label: 'Other', value: 'other' },
        ],
        gridCols: 'grid-item',
        required: true,
      },
      {
        name: 'additionalPhone',
        label: 'Additional Phone',
        type: 'tel',
        yupValidation: Yup.string().matches(
          /^[\+]?[0-9\s\-\(\)]{10,15}$/,
          'Invalid phone number'
        ),
        defaultValue: '',
        placeholder: '+919999999999',
        gridCols: 'grid-item',
      },
      {
        name: 'nextOfKinTitle',
        label: 'Next Of Kin Title',
        type: 'autocomplete',
        yupValidation: Yup.string(),
        defaultValue: '',
        options: [
          { label: 'Mr.', value: 'mr' },
          { label: 'Mrs.', value: 'mrs' },
          { label: 'Ms.', value: 'ms' },
          { label: 'Dr.', value: 'dr' },
        ],

        gridCols: 'grid-item',
      },
      {
        name: 'nextOfKinFirstName',
        label: 'Next Of Kin First Name',
        type: 'text',
        yupValidation: Yup.string().min(2, 'Minimum 2 characters'),
        defaultValue: '',
        placeholder: 'Enter Next Of Kin First Name',
        gridCols: 'grid-item',
      },
      {
        name: 'nextOfKinMiddleName',
        label: 'Next Of Kin Middle Name',
        type: 'text',
        yupValidation: Yup.string(),
        placeholder: 'Enter Next Of Kin Middle Name',

        defaultValue: '',
        gridCols: 'grid-item',
      },
      {
        name: 'nextOfKinLastName',
        label: 'Next Of Kin Last Name',
        type: 'text',
        yupValidation: Yup.string(),
        placeholder: 'Enter Next Of Kin Last Name',

        defaultValue: '',
        gridCols: 'grid-item',
      },
      {
        name: 'nextOfKinRelation',
        label: 'Next Of Kin Relation',
        type: 'autocomplete',
        yupValidation: Yup.string(),
        defaultValue: '',
        placeholder: 'Enter Next Of Kin Relation',
        options: [
          { label: 'Father', value: 'father' },
          { label: 'Mother', value: 'mother' },
          { label: 'Spouse', value: 'spouse' },
          { label: 'Sibling', value: 'sibling' },
          { label: 'Other', value: 'other' },
        ],
        gridCols: 'grid-item',
      },
      {
        name: 'nextOfKinPhoneNo',
        label: 'Next Of Kin Phone No.',
        type: 'tel',
        yupValidation: Yup.string().matches(
          /^[\+]?[0-9\s\-\(\)]{10,15}$/,
          'Invalid phone number'
        ),
        placeholder: '+919999999999',

        defaultValue: '',
        gridCols: 'grid-item',
      },
      {
        name: 'nextOfKinEmail',
        label: 'Next Of Kin Email',
        type: 'email',
        yupValidation: Yup.string(),
        placeholder: 'Enter Next Of Kin Email',
        defaultValue: '',
        gridCols: 'grid-item',
      },
      {
        name: 'nextOfKinAddress',
        label: 'Next of Kin Address',
        type: 'textarea',
        yupValidation: Yup.string(),
        placeholder: 'Enter Next Of Kin Address',

        defaultValue: '',
        gridCols: 'grid-item-wide',
      },
      {
        name: 'patientCategory',
        label: 'Patient Category',
        type: 'autocomplete',
        yupValidation: Yup.string(),
        defaultValue: '',
        options: [
          // { label: 'Dental', value: 'dental' },
          // { label: 'General', value: 'general' },
          // { label: 'Rural', value: 'rural' },
        ],
        gridCols: 'grid-item',
        required: true,
      },
      {
        name: 'caseFileNumber',
        label: 'Case File Number',
        type: 'text',
        yupValidation: Yup.string(),
        defaultValue: '',
        gridCols: 'grid-item',
      },
      {
        name: 'caseFileWith',
        label: 'Case File With',
        type: 'text',
        yupValidation: Yup.string(),
        defaultValue: '',
        gridCols: 'grid-item',
      },
      {
        name: 'vipPatient',
        label: 'VIP Patient',
        type: 'checkbox',
        defaultValue: false,
        yupValidation: Yup.boolean(),
        gridCols: 'grid-item',
      },
    ],
  },
  {
    title: 'Additional Patient Information',
    fields: [
      {
        name: 'address',
        label: 'Address',
        type: 'textarea',
        yupValidation: Yup.string(),
        defaultValue: '',
        gridCols: 'grid-item-wide',
        required: false,
      },
      {
        name: 'area',
        label: 'Area',
        type: 'text',
        yupValidation: Yup.string(),
        placeholder: 'Search Area',

        defaultValue: '',
        gridCols: 'grid-item',
      },
      {
        name: 'city',
        label: 'City',
        type: 'autocomplete',
        yupValidation: Yup.string().required('City is required'),
        // options: [{ label: 'Hyderabad', value: 'Hyderabad' }],
        required: true,
      },
      {
        name: 'country',
        label: 'Country',
        type: 'text',
        // options: [],
        yupValidation: Yup.string(),
        gridCols: 'grid-item',
        required: false,
        disabled: true,
      },
      {
        name: 'state',
        label: 'State',
        type: 'text',
        // options: [],
        yupValidation: Yup.string(),
        gridCols: 'grid-item',
        required: false,
        disabled: true,
      },

      {
        name: 'bloodGroup',
        label: 'Blood Group',
        type: 'autocomplete',
        yupValidation: Yup.string(),
        defaultValue: '',
        options: [
          { label: 'A+', value: 'A+' },
          { label: 'A-', value: 'A-' },
          { label: 'B+', value: 'B+' },
          { label: 'B-', value: 'B-' },
          { label: 'AB+', value: 'AB+' },
          { label: 'AB-', value: 'AB-' },
          { label: 'O+', value: 'O+' },
          { label: 'O-', value: 'O-' },
        ],
        gridCols: 'grid-item',
      },
      {
        name: 'maritalStatus',
        label: 'Marital Status',
        type: 'autocomplete',
        yupValidation: Yup.string(),
        defaultValue: '',
        options: [
          { label: 'Annuled', value: 'annuled' },
          { label: 'Common Law', value: 'common_law' },
          { label: 'Domestic Partner', value: 'domestic_partner' },
          { label: 'Single', value: 'single' },
          { label: 'Married', value: 'married' },
          { label: 'Divorced', value: 'divorced' },
          { label: 'Widowed', value: 'widowed' },
        ],
        gridCols: 'grid-item',
      },
      {
        name: 'religion',
        label: 'Religion',
        type: 'autocomplete',
        yupValidation: Yup.string(),
        defaultValue: '',
        options: [
          { label: 'Hindu', value: 'hindu' },
          { label: 'Muslim', value: 'muslim' },
          { label: 'Christian', value: 'christian' },
          { label: 'Other', value: 'other' },
        ],
        gridCols: 'grid-item',
      },
      {
        name: 'race',
        label: 'Race',
        type: 'autocomplete',
        yupValidation: Yup.string(),
        defaultValue: '',
        options: [
          { label: 'Asian', value: 'asian' },
          { label: 'Black', value: 'black' },
          { label: 'White', value: 'white' },
          { label: 'Hispanic', value: 'hispanic' },
          { label: 'Other', value: 'other' },
        ],
        gridCols: 'grid-item',
      },
      {
        name: 'occupation',
        label: 'Occupation',
        type: 'autocomplete',
        yupValidation: Yup.string(),
        options: [
          { label: 'ADNOC', value: 'adnoc' },
          { label: 'Armed Forces', value: 'armed_forces' },
          { label: 'Aviation', value: 'aviation' },
          { label: 'Energy', value: 'energy' },
          { label: 'Government', value: 'government' },
          { label: 'Health Sector', value: 'healthSector' },
          { label: 'Hospitality Industry', value: 'hospitalityIndustry' },
          { label: 'Judiciary', value: 'judiciary' },
          { label: 'Missions', value: 'missions' },
          { label: 'Official Delegates', value: 'officialDelegates' },
          { label: 'Others', value: 'others' },
          { label: 'Business', value: 'business' },
          { label: 'Retired', value: 'retired' },
          { label: 'Unemployed', value: 'unemployed' },
          { label: 'Other', value: 'other' },
        ],
        gridCols: 'grid-item',
      },
      {
        name: 'otherIdentificationDocument',
        label: 'Other Identification Document Type',
        type: 'autocomplete',
        yupValidation: Yup.string(),
        defaultValue: '',
        options: [
          { label: 'Passport', value: 'passport' },
          { label: 'Driving License', value: 'drivingLicense' },
          { label: 'Voter ID', value: 'voterId' },
          { label: 'PAN Card', value: 'panCard' },
          { label: 'Other', value: 'other' },
        ],

        gridCols: 'grid-item',
      },
      {
        name: 'otherIdentifierDocumentValue',
        label: 'Other Identifier Document Value',
        type: 'text',
        yupValidation: Yup.string(),
        defaultValue: '',
        gridCols: 'grid-item',
      },
      {
        name: 'govtIdType',
        label: 'Govt ID Type',
        type: 'autocomplete',
        yupValidation: Yup.string(),
        defaultValue: '',
        options: [
          { label: 'AADHAR', value: 'aadhar' },
          { label: 'PAN', value: 'pan' },
          { label: 'Voter ID', value: 'voter_id' },
        ],

        gridCols: 'grid-item',
      },
      {
        name: 'govtId',
        label: 'Govt ID',
        type: 'text',
        yupValidation: Yup.string(),
        defaultValue: '',
        gridCols: 'grid-item',
      },
      {
        name: 'remarks',
        label: 'Remarks',
        type: 'textarea',
        yupValidation: Yup.string(),
        defaultValue: '',
        gridCols: 'grid-item-wide',
      },
      {
        name: 'oldReg',
        label: 'Old Reg',
        type: 'text',
        yupValidation: Yup.string(),
        defaultValue: '',
        gridCols: 'grid-item',
      },
      {
        name: 'preferredLanguage',
        label: 'Preferred Language',
        type: 'autocomplete',
        yupValidation: Yup.string(),
        defaultValue: '',
        options: [
          { label: 'English', value: 'english' },
          { label: 'Hindi', value: 'hindi' },
          { label: 'Telugu', value: 'telugu' },
          { label: 'Tamil', value: 'tamil' },
          { label: 'Other', value: 'other' },
        ],

        gridCols: 'grid-item',
      },
      {
        name: 'email',
        label: 'Email',
        type: 'email',
        yupValidation: Yup.string(),
        defaultValue: '',
        gridCols: 'grid-item',
      },

      {
        name: 'dentalOPNo',
        label: 'Dental OP No',
        type: 'text',
        yupValidation: Yup.string(),
        defaultValue: '',
        gridCols: 'grid-item',
      },
      {
        name: 'abhaId',
        label: 'ABHA ID',
        type: 'text',
        yupValidation: Yup.string(),
        defaultValue: '',
        gridCols: 'grid-item',
      },
      // {
      //   name: 'patientCategory',
      //   label: 'Patient Category',
      //   type: 'text',
      //   yupValidation: Yup.string(),
      //   defaultValue: '',
      //   gridCols: 'grid-item',
      //   required: false,
      // },
      {
        name: 'patientSourcingCategory',
        label: 'Patient Sourcing Category',
        type: 'text',
        yupValidation: Yup.string(),
        defaultValue: '',
        gridCols: 'grid-item',
      },
    ],
  },
  {
    title: 'MODE OF COMMUNICATION',
    fields: [
      {
        name: 'modeOfCommunicationSMS',
        label: 'SMS',
        type: 'checkbox',
        defaultValue: false,
        yupValidation: Yup.boolean(),
        gridCols: 'grid-item', // you can use CSS grid to align
      },
      {
        name: 'modeOfCommunicationEMAIL',
        label: 'Email',
        type: 'checkbox',
        defaultValue: false,
        yupValidation: Yup.boolean(),
        gridCols: 'grid-item',
      },
    ],
  },

  {
    title: 'Custom Visit Information',
    fields: [
      {
        name: 'camp',
        label: 'CAMP',
        type: 'autocomplete',
        yupValidation: Yup.string(),
        defaultValue: '',
        options: [
          // { label: 'Camp A', value: 'campA' },
          // { label: 'Camp B', value: 'campB' },
        ],

        gridCols: 'grid-item-half',
      },
      {
        name: 'referralSource',
        label: 'Referral Source',
        type: 'autocomplete',
        yupValidation: Yup.string().required('Referral source is required'),
        defaultValue: '',
        options: [
          // { label: 'Doctor', value: 'doctor' },
          // { label: 'Hospital', value: 'hospital' },
          // { label: 'Self', value: 'self' },
        ],

        gridCols: 'grid-item-half',
        required: true,
      },
    ],
  },
]

export const IpRegistrationFormSections = (): {
  title: string
  fields: CreateVisitField[]
}[] => [
  {
    title: 'Patient Details',
    fields: [
      {
        name: 'newRegistration',
        label: 'New Registration: ',
        type: 'checkbox',
        yupValidation: Yup.boolean(),
        defaultValue: true,
      },
      {
        name: 'mrNoOrPhone',
        label: 'MR No/Name/Phone',
        type: 'text',
        yupValidation: Yup.string().nullable(),
        placeholder: 'Enter MR No / Name / Phone',
      },
      {
        name: 'medicoLegalCase',
        label: 'Medico Legal Case',
        type: 'checkbox',
        yupValidation: Yup.boolean(),
        defaultValue: false,
      },
      {
        name: 'previousVisitDoctor',
        label: 'Previous Visit Doctor',
        type: 'text',
        yupValidation: Yup.string().nullable(),
        placeholder: 'Enter Previous Visit Doctor',
      },
      {
        name: 'closePreviousActiveVisit',
        label: 'Close Previous Active Visit',
        type: 'checkbox',
        yupValidation: Yup.boolean(),
        defaultValue: false,
      },
    ],
  },
  {
    title: 'Medico Legal Case Details',
    fields: [
      {
        name: 'mlcDocument',
        label: 'MLC Document',
        type: 'autocomplete',
        options: [
          { label: 'FIR', value: 'FIR' },
          { label: 'Court Order', value: 'Court Order' },
          { label: 'Other', value: 'Other' },
        ],
        yupValidation: Yup.string(),
        required: false,
      },
      {
        name: 'mlcType',
        label: 'MLC Type',
        type: 'text',
        yupValidation: Yup.string(),
        required: false,
      },
      {
        name: 'accidentPlace',
        label: 'Accident Place',
        type: 'text',
        yupValidation: Yup.string(),
        required: false,
      },
      {
        name: 'policeStation',
        label: 'Police Station',
        type: 'text',
        yupValidation: Yup.string(),
        required: false,
      },
      {
        name: 'mlcRemarks',
        label: 'MLC Remarks',
        type: 'text',
        yupValidation: Yup.string(),
        required: false,
      },
      {
        name: 'certificateStatus',
        label: 'Certificate Status',
        type: 'text',
        yupValidation: Yup.string(),
        required: false,
      },
    ],
  },
  {
    title: 'Basic Information',
    fields: [
      {
        name: 'title',
        label: 'Title',
        type: 'autocomplete',
        yupValidation: Yup.string().required('Title is required'),
        options: [
          { label: 'B/O', value: 'B/O' },
          { label: 'Baby', value: 'Baby' },
          { label: 'Dr.', value: 'Dr.' },
          { label: 'Miss', value: 'Miss' },
          { label: 'Mr.', value: 'Mr.' },
          { label: 'Mrs.', value: 'Mrs.' },
          { label: 'Ms.', value: 'Ms.' },
        ],
        placeholder: 'Select Title',
        required: true,
      },
      {
        name: 'firstName',
        label: 'First Name',
        type: 'text',
        yupValidation: Yup.string()
          .required('First name is required')
          .min(2, 'Minimum 2 characters'),
        required: true,
      },
      {
        name: 'middleName',
        label: 'Middle Name',
        type: 'text',
        yupValidation: Yup.string(),
      },
      {
        name: 'lastName',
        label: 'Last Name',
        type: 'text',
        yupValidation: Yup.string()
          .required('Last name is required')
          .min(2, 'Minimum 2 characters'),
        required: true,
      },
      {
        name: 'mobile',
        label: 'Mobile',
        type: 'tel',
        yupValidation: Yup.string()
          .required('Mobile is required')
          .matches(/^[\+]?[0-9\s\-\(\)]{10,15}$/, 'Invalid phone number'),
        placeholder: '+91 9999999999',
        required: true,
      },
      {
        name: 'dateOfBirth',
        label: 'Date of Birth',
        type: 'date',
        yupValidation: Yup.string().required('Date of birth is required'),
        required: true,
      },
      {
        name: 'age',
        label: 'Age',
        type: 'number',
        yupValidation: Yup.string().required('Age is required'),
        required: true,
      },
      {
        name: 'gender',
        label: 'Gender',
        type: 'autocomplete',
        yupValidation: Yup.string().required('Gender is required'),
        options: [
          { label: 'Male', value: 'Male' },
          { label: 'Female', value: 'Female' },
          { label: 'Other', value: 'Other' },
        ],
        required: true,
      },
      {
        name: 'additionalPhone',
        label: 'Additional Phone',
        type: 'tel',
        yupValidation: Yup.string().matches(
          /^[\+]?[0-9\s\-\(\)]{10,15}$/,
          'Invalid phone number'
        ),
        placeholder: '+91 9999999999',
      },
      {
        name: 'patientCategory',
        label: 'Patient Category',
        type: 'autocomplete',
        yupValidation: Yup.string(),
        options: [
          { label: 'Dental', value: 'Dental' },
          { label: 'General', value: 'General' },
          { label: 'Rural', value: 'Rural' },
        ],
        required: true,
      },
      {
        name: 'caseFile',
        label: 'Case File',
        type: 'text',
        yupValidation: Yup.string(),
      },
      {
        name: 'auto',
        label: 'Auto',
        type: 'checkbox',
        yupValidation: Yup.boolean(),
      },
    ],
  },
  {
    title: 'Next Of Kin Details',
    fields: [
      {
        name: 'nextOfKinTitle',
        label: 'Next Of Kin Title',
        type: 'autocomplete',
        yupValidation: Yup.string(),
        options: [
          { label: 'Mr.', value: 'Mr.' },
          { label: 'Mrs.', value: 'Mrs.' },
          { label: 'Ms.', value: 'Ms.' },
          { label: 'Dr.', value: 'Dr.' },
        ],
      },
      {
        name: 'nextOfKinFirstName',
        label: 'Next Of Kin First Name',
        type: 'text',
        yupValidation: Yup.string().min(2, 'Minimum 2 characters'),
        placeholder: 'Enter Next Of Kin First Name',
      },
      {
        name: 'nextOfKinMiddleName',
        label: 'Next Of Kin Middle Name',
        type: 'text',
        yupValidation: Yup.string(),
        placeholder: 'Enter Next Of Kin Middle Name',
      },
      {
        name: 'nextOfKinLastName',
        label: 'Next Of Kin Last Name',
        type: 'text',
        yupValidation: Yup.string(),
        placeholder: 'Enter Next Of Kin Last Name',
      },
      {
        name: 'nextOfKinRelation',
        label: 'Next Of Kin Relation',
        type: 'autocomplete',
        yupValidation: Yup.string(),
        placeholder: 'Enter Next Of Kin Relation',
        options: [
          { label: 'Father', value: 'Father' },
          { label: 'Mother', value: 'Mother' },
          { label: 'Spouse', value: 'Spouse' },
          { label: 'Sibling', value: 'Sibling' },
          { label: 'Other', value: 'Other' },
        ],
      },
      {
        name: 'nextOfKinPhoneNo',
        label: 'Next Of Kin Phone No.',
        type: 'tel',
        yupValidation: Yup.string().matches(
          /^[\+]?[0-9\s\-\(\)]{10,15}$/,
          'Invalid phone number'
        ),
        placeholder: '+91 9999999999',
      },
      {
        name: 'nextOfKinEmail',
        label: 'Next Of Kin Email',
        type: 'email',
        yupValidation: Yup.string(),
        placeholder: 'Enter Next Of Kin Email',
      },
      {
        name: 'nextOfKinAddress',
        label: 'Next of Kin Address',
        type: 'textarea',
        yupValidation: Yup.string(),
        placeholder: 'Enter Next Of Kin Address',
      },
    ],
  },
  {
    title: 'Additional Patient Information',
    fields: [
      {
        name: 'address',
        label: 'Address',
        type: 'textarea',
        yupValidation: Yup.string(),
        required: false,
      },
      {
        name: 'area',
        label: 'Area',
        type: 'text',
        yupValidation: Yup.string(),
        placeholder: 'Search Area',
      },
      {
        name: 'city',
        label: 'City',
        type: 'autocomplete',
        yupValidation: Yup.string().required('City is required'),
        options: [],
        required: true,
      },

      {
        name: 'country',
        label: 'Country',
        type: 'autocomplete',
        yupValidation: Yup.string().required('Country is required'),
        options: [],
        required: true,
      },
      {
        name: 'state',
        label: 'State',
        type: 'autocomplete',
        yupValidation: Yup.string().required('State is required'),
        options: [],
        required: true,
      },

      {
        name: 'bloodGroup',
        label: 'Blood Group',
        type: 'autocomplete',
        yupValidation: Yup.string(),
        options: [
          { label: 'A+', value: 'A+' },
          { label: 'A-', value: 'A-' },
          { label: 'B+', value: 'B+' },
          { label: 'B-', value: 'B-' },
          { label: 'AB+', value: 'AB+' },
          { label: 'AB-', value: 'AB-' },
          { label: 'O+', value: 'O+' },
          { label: 'O-', value: 'O-' },
        ],
      },
      {
        name: 'maritalStatus',
        label: 'Marital Status',
        type: 'autocomplete',
        yupValidation: Yup.string(),
        options: [
          { label: 'Annuled', value: 'Annuled' },
          { label: 'Common Law', value: 'Common Law' },
          { label: 'Domestic Partner', value: 'Domestic Partner' },
          { label: 'Single', value: 'Single' },
          { label: 'Married', value: 'Married' },
          { label: 'Divorced', value: 'Divorced' },
          { label: 'Widowed', value: 'Widowed' },
        ],
      },
      {
        name: 'religion',
        label: 'Religion',
        type: 'autocomplete',
        yupValidation: Yup.string(),
        options: [
          { label: 'Hindu', value: 'Hindu' },
          { label: 'Muslim', value: 'Muslim' },
          { label: 'Christian', value: 'Christian' },
          { label: 'Other', value: 'Other' },
        ],
      },
      {
        name: 'race',
        label: 'Race',
        type: 'autocomplete',
        yupValidation: Yup.string(),
        options: [
          { label: 'Asian', value: 'Asian' },
          { label: 'Black', value: 'Black' },
          { label: 'White', value: 'White' },
          { label: 'Hispanic', value: 'Hispanic' },
          { label: 'Other', value: 'Other' },
        ],
      },
      {
        name: 'occupation',
        label: 'Occupation',
        type: 'autocomplete',
        yupValidation: Yup.string(),
        options: [
          { label: 'ADNOC', value: 'ADNOC' },
          { label: 'Armed Forces', value: 'Armed Forces' },
          { label: 'Aviation', value: 'Aviation' },
          { label: 'Energy', value: 'Energy' },
          { label: 'Government', value: 'Government' },
          { label: 'Health Sector', value: 'Health Sector' },
          { label: 'Hospitality Industry', value: 'Hospitality Industry' },
          { label: 'Judiciary', value: 'Judiciary' },
          { label: 'Missions', value: 'Missions' },
          { label: 'Official Delegates', value: 'Official Delegates' },
          { label: 'Others', value: 'Others' },
          { label: 'Business', value: 'Business' },
          { label: 'Retired', value: 'Retired' },
          { label: 'Unemployed', value: 'Unemployed' },
          { label: 'Other', value: 'Other' },
        ],
      },
      {
        name: 'dentalOPNo',
        label: 'Dental OP No',
        type: 'text',
        yupValidation: Yup.string(),
      },
      {
        name: 'abhaId',
        label: 'ABHA ID',
        type: 'text',
        yupValidation: Yup.string(),
      },
      {
        name: 'govtIdType',
        label: 'Govt ID Type',
        type: 'autocomplete',
        yupValidation: Yup.string(),
        options: [
          { label: 'AADHAR', value: 'AADHAR' },
          { label: 'PAN', value: 'PAN' },
          { label: 'Voter ID', value: 'Voter ID' },
        ],
      },
      {
        name: 'govtId',
        label: 'Govt ID',
        type: 'text',
        yupValidation: Yup.string(),
      },
      {
        name: 'otherIdentificationDocument',
        label: 'Other Identification Document Type',
        type: 'autocomplete',
        yupValidation: Yup.string(),
        options: [
          { label: 'Passport', value: 'Passport' },
          { label: 'Driving License', value: 'Driving License' },
          { label: 'Voter ID', value: 'Voter ID' },
          { label: 'PAN Card', value: 'PAN Card' },
          { label: 'Other', value: 'Other' },
        ],
      },
      {
        name: 'otherIdentifierDocumentValue',
        label: 'Other Identifier Document Value',
        type: 'text',
        yupValidation: Yup.string(),
      },
      {
        name: 'remarks',
        label: 'Remarks',
        type: 'textarea',
        yupValidation: Yup.string(),
      },
      {
        name: 'email',
        label: 'Email',
        type: 'email',
        yupValidation: Yup.string(),
      },
      {
        name: 'preferredLanguage',
        label: 'Preferred Language',
        type: 'autocomplete',
        yupValidation: Yup.string(),
        options: [
          { label: 'English', value: 'English' },
          { label: 'Hindi', value: 'Hindi' },
          { label: 'Telugu', value: 'Telugu' },
          { label: 'Tamil', value: 'Tamil' },
          { label: 'Other', value: 'Other' },
        ],
      },
      {
        name: 'patientPhoto',
        label: 'Patient Photo',
        type: 'file',
        yupValidation: Yup.mixed(),
        defaultValue: null,
        placeholder: 'Upload Patient Photo',
        accept: 'image/*',
      },
      {
        name: 'oldReg',
        label: 'Old Reg',
        type: 'text',
        yupValidation: Yup.string(),
      },
      {
        name: 'vipPatient',
        label: 'VIP Patient',
        type: 'checkbox',
        defaultValue: false,
        yupValidation: Yup.boolean(),
      },
    ],
  },
  {
    title: 'MODE OF COMMUNICATION',
    fields: [
      {
        name: 'modeOfCommunicationSMS',
        label: 'SMS',
        type: 'checkbox',
        defaultValue: false,
        yupValidation: Yup.boolean(),
      },
      {
        name: 'modeOfCommunicationEMAIL',
        label: 'Email',
        type: 'checkbox',
        defaultValue: false,
        yupValidation: Yup.boolean(),
      },
    ],
  },
  {
    title: 'Additional Visit Information',
    fields: [
      {
        name: 'camp',
        label: 'CAMP',
        type: 'autocomplete',
        yupValidation: Yup.string(),
        options: [
          { label: 'Camp A', value: 'Camp A' },
          { label: 'Camp B', value: 'Camp B' },
        ],
      },
      {
        name: 'referralSource',
        label: 'Referral Source',
        type: 'autocomplete',
        yupValidation: Yup.string().required('Referral source is required'),
        options: [
          { label: 'Doctor', value: 'doctor' },
          { label: 'Hospital', value: 'hospital' },
          { label: 'Self', value: 'self' },
        ],
        required: true,
      },
    ],
  },
  {
    title: 'Sponsor Information',
    fields: [
      {
        name: 'primarySponsor',
        label: 'Primary Sponsor',
        type: 'checkbox',
        yupValidation: Yup.string(),
        defaultValue: false,
      },
      {
        name: 'secondarySponsor',
        label: 'Secondary Sponsor',
        type: 'checkbox',
        yupValidation: Yup.string(),
        defaultValue: false,
      },
    ],
  },
  {
    title: 'Primary Sponsor',
    fields: [
      {
        name: 'pSponsor',
        label: 'Sponsor',
        type: 'text',
        yupValidation: Yup.string(),
        defaultValue: '',
      },
      {
        name: 'insuranceCo',
        label: 'Insurance Co: ',
        type: 'autocomplete',
        yupValidation: Yup.string(),
        placeholder: '--select--',
      },
    ],
  },
  {
    title: 'Secondary Sponsor',
    fields: [
      {
        name: 'sSponsor',
        label: 'Sponsor',
        type: 'text',
        yupValidation: Yup.string(),
        defaultValue: '',
      },
      {
        name: 'insuranceCo',
        label: 'Insurance Co: ',
        type: 'autocomplete',
        yupValidation: Yup.string(),
        placeholder: '--select--',
      },
    ],
  },
  {
    title: 'Payment Information',
    fields: [
      {
        name: 'billType',
        label: 'Bill Type',
        type: 'autocomplete',
        yupValidation: Yup.string(),
        options: [{ label: 'Bill Now', value: 'Bill Now' }],
      },
      {
        name: 'ratePlan',
        label: 'Rate Plan',
        type: 'autocomplete',
        yupValidation: Yup.string().required('Rate Plan is required'),
        options: [{ label: 'GENERAL', value: 'GENERAL' }],
        required: true,
      },
      {
        name: 'availableCreditLimit',
        label: 'Available Credit Plan',
        type: 'text',
        yupValidation: Yup.string().required('Credit Plan is required'),
        options: [{ label: 'GENERAL', value: 'GENERAL' }],
        required: true,
      },
    ],
  },
  {
    title: 'Admission Information',
    fields: [
      {
        name: 'department',
        label: 'Department',
        type: 'autocomplete',
        yupValidation: Yup.string(),
        options: [
          { label: 'A R T Center', value: 'A R T Center' },
          { label: 'AC Maintenance', value: 'AC Maintenance' },
          { label: 'AC Ward', value: 'AC Ward' },
          { label: 'ANAESTHESIOLOGY', value: 'ANAESTHESIOLOGY' },
          { label: 'ASPIRE HALL', value: 'ASPIRE HALL' },
          { label: 'ASSET', value: 'ASSET' },
          { label: 'ATRI BLOCK', value: 'ATRI BLOCK' },
          { label: 'AUDITORIUM', value: 'AUDITORIUM' },
          { label: 'AYUVU BHAKOSA', value: 'AYUVU BHAKOSA' },
          { label: 'BIOCHEMISTRY', value: 'BIOCHEMISTRY' },
          { label: 'BLOOD BANK', value: 'BLOOD BANK' },
          { label: 'BOYS HOSTEL-1', value: 'BOYS HOSTEL-1' },
          { label: 'BOYS HOSTEL-2', value: 'BOYS HOSTEL-2' },
          { label: 'BSC NURSING BLOCK', value: 'BSC NURSING BLOCK' },
          { label: 'CARDIOLOGY', value: 'CARDIOLOGY' },
          { label: 'CARDIOTHORACIC SURGERY', value: 'CARDIOTHORACIC SURGERY' },
          { label: 'CENTRAL KITCHEN', value: 'CENTRAL KITCHEN' },
          {
            label: 'CONSERVATIVE DENTISTRY & ENDODONTICS',
            value: 'CONSERVATIVE DENTISTRY & ENDODONTICS',
          },
          { label: 'CRITICAL CARE', value: 'CRITICAL CARE' },
        ],
      },
      {
        name: 'unit',
        label: 'Unit',
        type: 'autocomplete',
        yupValidation: Yup.string(),
        options: [{ label: 'Unit options', value: 'Unit options' }],
      },
      {
        name: 'admittingDoctor',
        label: 'Admitting Doctor',
        type: 'text',
        yupValidation: Yup.string().required('Admitting Doctor is required'),
        required: true,
      },
      {
        name: 'transferredFrom',
        label: 'Transferred From',
        type: 'autocomplete',
        yupValidation: Yup.string(),
        options: [
          { label: 'Transferred options', value: 'Transferred options' },
        ],
      },
      {
        name: 'bedName',
        label: 'Bed Name',
        type: 'autocomplete',
        yupValidation: Yup.string(),
        options: [{ label: 'bed options', value: 'bed options' }],
      },
      {
        name: 'bedType',
        label: 'BedType',
        type: 'autocomplete',
        options: [
          { label: 'BURNS ICU', value: 'BURNS ICU' },
          { label: 'DIALYSIS', value: 'DIALYSIS' },
          { label: 'GENERAL', value: 'GENERAL' },
          { label: 'GENERAL WARD', value: 'GENERAL WARD' },
          { label: 'GENERAL WARD A/C', value: 'GENERAL WARD A/C' },
          { label: 'HDU', value: 'HDU' },
          { label: 'ICCU', value: 'ICCU' },
          { label: 'ICU', value: 'ICU' },
          { label: 'NICU', value: 'NICU' },
          { label: 'OBICU', value: 'OBICU' },
          { label: 'PICU', value: 'PICU' },
          { label: 'POST OPERATIVE ICU', value: 'POST OPERATIVE ICU' },
          { label: 'PRIVATE ROOM', value: 'PRIVATE ROOM' },
          { label: 'RICU', value: 'RICU' },
          { label: 'SICU', value: 'SICU' },
        ],
        yupValidation: Yup.string().required('BedType is required'),
        required: true,
      },
      {
        name: 'ward',
        label: 'Ward',
        type: 'autocomplete',
        yupValidation: Yup.string().required('Ward is required'),
        options: [{ label: 'Ward xyz', value: 'Ward xyz' }],
        required: true,
      },
      {
        name: 'dutyDoctor',
        label: 'Duty Doctor',
        type: 'autocomplete',
        yupValidation: Yup.string(),
        options: [{ label: 'Bharath Kumar Boya', value: 'Bharath Kumar Boya' }],
      },
      {
        name: 'bedsAvailable',
        label: 'Beds Available',
        type: 'text',
        yupValidation: Yup.string(),
      },
      {
        name: 'isDaycare',
        label: 'Is Daycare',
        type: 'checkbox',
        yupValidation: Yup.boolean(),
        defaultValue: false,
      },
      {
        name: 'charge',
        label: 'Charge',
        type: 'text',
        yupValidation: Yup.string(),
      },
      {
        name: 'admissionType',
        label: 'Admission Type',
        type: 'autocomplete',
        yupValidation: Yup.string(),
        options: [{ label: 'IP', value: 'IP' }],
      },
      {
        name: 'referredBy',
        label: 'Referred By',
        type: 'text',
        yupValidation: Yup.string(),
      },
      {
        name: 'complaint',
        label: 'Complaint',
        type: 'text',
        yupValidation: Yup.string(),
      },
    ],
  },
]

export const PAGE_SIZE_ARRAY = [10, 20, 30, 50, 100]

export const APP_FLOW_NAMES = {
  OPD: 'opd',
  IPD: 'ipd',
  ER: 'er',
}

export const APP_FLOW_TYPES = {
  OPDTYPE: 'OP_VISIT',
  IPTYPE: 'IP_VISIT',
}

export const FILTER_TERMS = {
  ALL: 'ALL',
  OPD: 'OPD',
  IPD: 'IPD',
  ER: 'ER',
}

export const BILLING_TYPE_OPTIONS = {
  billNow: 'BILL_NOW',
  billLater: 'BILL_LATER',
}

export const VISIT_PAYMENT_STATUS = {
  paid: 'paid',
}

export const VISIT_STATUS = {
  inProgress: 'In Progress',
  transferred: 'Transferred',
  cancelled: 'Cancelled',
  closed: 'Closed',
  completed: 'Completed',
  scheduled: 'Scheduled',
}

export const TRANSFER_TYPES = {
  pay: 'pay',
  move: 'move',
  close: 'close',
}
export const ODOO_PAYMENT_STATUS = {
  paid: 'paid',
  notPaid: 'not_paid',
}

export const renderOdooPaymentStatus = (status: string) => {
  switch (status) {
    case ODOO_PAYMENT_STATUS.paid:
      return 'Paid'
    case ODOO_PAYMENT_STATUS.notPaid:
      return 'Not Paid'
    default:
      return status
  }
}

export const CONSULTATION_TYPES = {
  OPD: 'OPD',
  EMERGENCY: 'Emergency',
}
export interface OptionData {
  label: string
  value: string | number
  [key: string]: any
}

// Visit Role Options
export const VISIT_ROLE_OPTIONS: OptionData[] = [
  { label: 'Doctor', value: 'doctor' },
  { label: 'Nurse', value: 'nurse' },
]

export const getGenderShort = (gender?: string): string => {
  switch (gender?.toLowerCase()) {
    case 'male':
      return 'M'
    case 'female':
      return 'F'
    case 'others':
    case 'other':
      return 'O'
    default:
      return ''
  }
}

export const BED_STATUS_OCCUPIED = 'RESERVED'
export const BED_STATUS_AVAILABLE = 'ACTIVE'
export const BED_STATUS_BLOCKED = 'BLOCKED'

export const VISIT_SCOPES = {
  ACTIVE: 'ACTIVE',
  ALL: 'ALL',
  INACTIVE: 'INACTIVE',
}
